// frontend/src/utils/auth.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi, permissionsApi, planApi, totpApi } from './api';

const DEVICE_TOKEN_KEY = 'eg_device_token';

const AuthContext = createContext(null);

const ROLE_LEVELS = { admin:4, manager:3, staff:2, gm:2, freelance:1 };

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [perms, setPerms]   = useState({});
  const [features, setFeatures] = useState({}); // { 'bookings.view': true, ... }

  useEffect(() => {
    const token = localStorage.getItem('eg_token');
    if (!token) { setLoading(false); return; }
    authApi.me()
      .then(r => {
        setUser(r.data.user);
        // Charger les permissions effectives
        return permissionsApi.me();
      })
      .then(r => {
        setPerms(r.data.permissions || {});
        // Charger les features du plan
        return planApi.current().catch(() => ({ data: { plan: null } }));
      })
      .then(r => {
        const feats = {};
        for (const f of (r.data.plan?.features || [])) {
          if (f.feature) feats[f.feature] = { enabled: f.enabled, limit: f.limit };
        }
        setFeatures(feats);
      })
      .catch(() => localStorage.removeItem('eg_token'))
      .finally(() => setLoading(false));
  }, []);

  // Étape 1 : email + mot de passe.
  // Retourne les données de réponse pour laisser Login.jsx gérer le cas
  // "2FA requise" (requiresTotp / requiresTotpSetup) sans rediriger.
  const login = useCallback(async (email, password) => {
    const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY) || undefined;
    const r = await authApi.login({ email, password, deviceToken });
    if (r.data.token) {
      localStorage.setItem('eg_token', r.data.token);
      window.location.replace('/app');
      return r.data;
    }
    return r.data; // { requiresTotp } ou { requiresTotpSetup, challengeToken }
  }, []);

  // Étape 2 : code TOTP (ou code de secours) sur un compte déjà enrôlé.
  const completeTotpLogin = useCallback(async (challengeToken, code, isBackupCode, remember) => {
    const r = await totpApi.verify({ challengeToken, code, isBackupCode, remember });
    if (r.data.deviceToken) localStorage.setItem(DEVICE_TOKEN_KEY, r.data.deviceToken);
    localStorage.setItem('eg_token', r.data.token);
    window.location.replace('/app');
    return r.data;
  }, []);

  // Étape 2 bis : 2FA imposée par l'organisation, pas encore configurée —
  // termine l'enrôlement (secret généré côté client via /totp/setup avant appel).
  const completeTotpSetup = useCallback(async (challengeToken, secret, code, remember) => {
    const r = await totpApi.setupVerify({ challengeToken, secret, code, remember });
    if (r.data.deviceToken) localStorage.setItem(DEVICE_TOKEN_KEY, r.data.deviceToken);
    localStorage.setItem('eg_token', r.data.token);
    return r.data; // { backupCodes } à afficher avant redirection
  }, []);

  // Connexion via clé d'accès (passkey) : le flux WebAuthn (navigator.credentials)
  // est géré côté composant (Login.jsx) — ici on ne fait que stocker le token
  // déjà vérifié côté serveur et rediriger, comme pour le login classique.
  const loginWithPasskey = useCallback((token) => {
    localStorage.setItem('eg_token', token);
    window.location.replace('/app');
  }, []);

  // Connexion "en tant qu'utilisateur" depuis le SuperAdmin (voir
  // pages/Impersonate.jsx) : même stockage qu'un login normal, plus un flag
  // pour afficher le bandeau d'avertissement dans Layout.jsx.
  const startImpersonation = useCallback((token) => {
    localStorage.setItem('eg_token', token);
    localStorage.setItem('eg_impersonating', '1');
    window.location.replace('/app');
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('eg_token');
    localStorage.removeItem('eg_impersonating');
    window.location.replace('/login');
  }, []);

  const refreshPerms = useCallback(async () => {
    try {
      const r = await permissionsApi.me();
      setPerms(r.data.permissions || {});
    } catch {}
  }, []);

  // Mapping permission → feature plan requise
  const PERM_TO_FEATURE = {
    'cash.view': 'cash', 'cash.count': 'cash',
    'reports.view': 'reports',
    'schedule.view': 'schedule', 'schedule.edit': 'schedule',
    'stock.view': 'stock', 'stock.edit': 'stock',
    'permissions': 'permissions',
  };

  const can = useCallback((arg) => {
    if (!user) return false;
    const userLevel = ROLE_LEVELS[user.role] || 0;

    // Code de permission (contient un point)
    if (arg && arg.includes('.')) {
      // Vérifier la feature du plan si applicable
      const requiredFeature = PERM_TO_FEATURE[arg];
      if (requiredFeature && Object.keys(features).length > 0) {
        const feat = features[requiredFeature];
        if (feat && !feat.enabled) return false;
      }
      // Vérifier dans les permissions effectives
      if (arg in perms) return perms[arg];
      return userLevel >= 4;
    }

    // Rôle minimum
    return userLevel >= (ROLE_LEVELS[arg] || 0);
  }, [user, perms, features]);

  // Vérifie si on peut créer un objet selon les limites du plan
  // canCreate('max_rooms', currentRoomCount)
  const canCreate = useCallback((featureName, currentCount = 0) => {
    if (!features || !Object.keys(features).length) return true; // pas de plan = pas de limite
    const feat = features[featureName];
    if (!feat) return true;
    if (!feat.enabled) return false;
    if (feat.limit !== null && feat.limit !== undefined) {
      return currentCount < feat.limit;
    }
    return true;
  }, [features]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPasskey, startImpersonation, logout, can, canCreate, perms, features, refreshPerms }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
